/**
 * The last line of defence against a blank screen.
 *
 * React unmounts the entire tree when a render throws, and inside Telegram that shows as a grey
 * rectangle with no way out but killing the app — the user cannot open devtools, cannot see a
 * stack, and has no reason to believe reloading would help. A settings change did exactly that
 * (see DECISIONS.md D33): one bad cache write, and the whole app disappeared.
 *
 * The underlying bug is fixed. This exists so the *class* of failure cannot recur silently: any
 * future render error shows something a person can act on and a button that recovers.
 *
 * Deliberately a class component — `componentDidCatch` has no hook equivalent, and React's own
 * documentation still points here. Deliberately dependency-free and inline-styled: a boundary
 * that needs the design system to render is a boundary that fails when the design system is
 * what broke.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Called with the error so the app can report it. Must never throw. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep the stack in the console: this is the only trace a developer gets from a phone.
    console.error('[promptvault] render error', error, info.componentStack);
    try {
      this.props.onError?.(error, info);
    } catch {
      /* a failing reporter must not replace the error it was reporting */
    }
  }

  private reload = (): void => {
    window.location.href = '/';
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Telegram theme variables are set on :root by the platform before our JS runs, so these
    // stay readable in light and dark even though no stylesheet is involved.
    return (
      <div
        role="alert"
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 24,
          textAlign: 'center',
          background: 'var(--tg-theme-bg-color, #fff)',
          color: 'var(--tg-theme-text-color, #000)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <div style={{ fontSize: 34, lineHeight: 1 }} aria-hidden>
          ⚠️
        </div>
        <div style={{ fontSize: 17, fontWeight: 600 }}>Something went wrong</div>
        <div
          style={{
            fontSize: 15,
            maxWidth: 300,
            color: 'var(--tg-theme-hint-color, #8e8e93)',
          }}
        >
          This screen failed to load. Reloading usually fixes it.
        </div>
        <button
          type="button"
          onClick={this.reload}
          style={{
            marginTop: 8,
            padding: '11px 22px',
            borderRadius: 10,
            border: 'none',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            background: 'var(--tg-theme-button-color, #007aff)',
            color: 'var(--tg-theme-button-text-color, #fff)',
          }}
        >
          Reload
        </button>
        {/* The message, not the stack: enough for a support thread, no wall of minified code. */}
        <div
          style={{
            marginTop: 14,
            fontSize: 12,
            fontFamily: 'ui-monospace, monospace',
            color: 'var(--tg-theme-hint-color, #8e8e93)',
            wordBreak: 'break-word',
            maxWidth: 320,
          }}
        >
          {error.message}
        </div>
      </div>
    );
  }
}
